import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';
import { logger } from '@/lib/logger';
import { db } from '@/lib/firebase';
import { useActiveTeamId } from '@/contexts/TeamContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  getTeamOnboarding,
  getUserOnboardingPrefs,
  markChecklistItem,
  dismissFirstRunBanner,
  isFirstRun,
  hasDismissedBanner,
} from '@/services/onboarding.service';
import { TeamOnboarding, OnboardingChecklistItem } from '@/types/onboarding';

/**
 * Hook to get team onboarding state with real-time updates
 */
export function useTeamOnboarding() {
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['onboarding', 'team', teamId],
    queryFn: () => getTeamOnboarding(teamId!),
    enabled: !!teamId,
    staleTime: 0, // Always fetch fresh data
  });

  // Real-time listener - only subscribe when teamId is available
  useEffect(() => {
    if (!teamId) return;

    const docRef = doc(db, 'teams', teamId, 'settings', 'onboarding');

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as TeamOnboarding;
          queryClient.setQueryData(['onboarding', 'team', teamId], data);
        }
      },
      (error) => {
        // Handle permission errors gracefully - don't spam console
        if (error.code !== 'permission-denied') {
          logger.error('[useTeamOnboarding] Snapshot error:', error);
        }
      }
    );

    return () => unsubscribe();
  }, [teamId, queryClient]);

  return {
    ...query,
    isFirstRun: isFirstRun(query.data ?? null),
  };
}

/**
 * Hook to get user onboarding preferences
 */
export function useUserOnboardingPrefs() {
  const { user } = useAuth();
  const userId = user?.uid;

  const query = useQuery({
    queryKey: ['onboarding', 'user', userId],
    queryFn: () => getUserOnboardingPrefs(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    ...query,
    hasDismissedBanner: hasDismissedBanner(query.data ?? { bannerDismissedAt: null }),
  };
}

/**
 * Hook to mark a checklist item as complete
 */
export function useMarkChecklistItem() {
  const teamId = useActiveTeamId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (item: OnboardingChecklistItem) => markChecklistItem(teamId, item),
    onSuccess: () => {
      // Invalidate team onboarding query to refetch
      void void queryClient.invalidateQueries({ queryKey: ['onboarding', 'team', teamId] });
    },
  });
}

/**
 * Hook to dismiss the first-run banner
 */
export function useDismissFirstRunBanner() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => dismissFirstRunBanner(userId!),
    onSuccess: () => {
      // Invalidate user prefs query to refetch
      void void queryClient.invalidateQueries({ queryKey: ['onboarding', 'user', userId] });
    },
  });
}

/**
 * Combined hook for onboarding state
 * Returns both team and user onboarding data
 */
export function useOnboarding() {
  const teamOnboarding = useTeamOnboarding();
  const userPrefs = useUserOnboardingPrefs();
  const markItem = useMarkChecklistItem();
  const dismissBanner = useDismissFirstRunBanner();

  return {
    // Team state
    teamOnboarding: teamOnboarding.data,
    isFirstRun: teamOnboarding.isFirstRun,
    isLoadingTeam: teamOnboarding.isLoading,

    // User state
    userPrefs: userPrefs.data,
    hasDismissedBanner: userPrefs.hasDismissedBanner,
    isLoadingUser: userPrefs.isLoading,

    // Actions
    markItem: markItem.mutate,
    dismissBanner: dismissBanner.mutate,

    // Loading state
    isLoading: teamOnboarding.isLoading || userPrefs.isLoading,
  };
}
